import type { QltSlotAttendance, QltSlotAttendanceStudent, SsaSubStatus } from '../../api/types';
import type { AttendanceDraft } from '../../stores/qlt/slot-attendance';
import { StudentPortrait } from './StudentPortrait';

export interface AttendanceRowProps {
  row: QltSlotAttendance;
  value: AttendanceDraft;
  onChange: (value: AttendanceDraft) => void;
}

interface PillSpec {
  label: string;
  tone: 'present' | 'late' | 'excused' | 'unexcused';
}

const PILLS: Record<SsaSubStatus, PillSpec | null> = {
  none: null,
  onTime: { label: 'Có mặt - đúng giờ', tone: 'present' },
  lateTime: { label: 'Có mặt - đi muộn', tone: 'late' },
  excused: { label: 'Vắng mặt - có phép', tone: 'excused' },
  unexcused: { label: 'Vắng mặt - không phép', tone: 'unexcused' },
};

// dob is not in the minimal student type but the roster payload carries it when the school fills it in
function formatDob(student: QltSlotAttendanceStudent): string {
  const value = (student as { dob?: string }).dob;
  if (!value) return '';
  const day = String(value).slice(0, 10).split('-');
  return day.length === 3 ? `${day[2]}/${day[1]}/${day[0]}` : '';
}

export function AttendanceRow({ row, value, onChange }: AttendanceRowProps) {
  const name = row.student.name || 'Học sinh';
  const present = value.status === 'present';
  const absent = value.status === 'absent';
  // pill and both segment groups read the same draft, so a selected button always matches the label
  const onTime = present && value.subStatus === 'onTime';
  const late = present && value.subStatus === 'lateTime';
  const excused = absent && value.subStatus === 'excused';
  const unexcused = absent && value.subStatus === 'unexcused';
  const dob = formatDob(row.student);
  const pill = PILLS[value.subStatus];

  function pick(status: AttendanceDraft['status'], subStatus: SsaSubStatus) {
    onChange({ status, subStatus });
  }

  return (
    <li className="cm-roster__row">
      <StudentPortrait avatar={row.student.avatar} name={name} />
      <div className="cm-identity">
        <strong title={name}>{name}</strong>
        {/* one meta line so a two-line name keeps the row height */}
        {dob || row.student.code ? <small>{[dob, row.student.code].filter(Boolean).join(' · ')}</small> : null}
        {row.absentSource ? <em>{row.reason || 'Có đơn xin phép'}</em> : null}
      </div>
      <div className="cm-status">
        {pill ? <span className={`cm-status__pill cm-status__pill--${pill.tone}`}>{pill.label}</span> : null}
        <div className="cm-segments" role="group" aria-label={`Điểm danh ${name}`}>
          <button type="button" aria-pressed={present} className={`cm-seg cm-seg--present${present ? ' is-on' : ''}`} onClick={() => pick('present', 'onTime')}>Có mặt</button>
          <button type="button" aria-pressed={absent} className={`cm-seg cm-seg--absent${absent ? ' is-on' : ''}`} onClick={() => pick('absent', 'excused')}>Vắng mặt</button>
          <span className="cm-segments__rule" aria-hidden={true} />
          {absent ? (
            <>
              <button type="button" aria-pressed={excused} className={`cm-seg cm-seg--excused${excused ? ' is-on' : ''}`} onClick={() => pick('absent', 'excused')}>Có phép</button>
              <button type="button" aria-pressed={unexcused} className={`cm-seg cm-seg--unexcused${unexcused ? ' is-on' : ''}`} onClick={() => pick('absent', 'unexcused')}>Không phép</button>
            </>
          ) : (
            <>
              <button type="button" aria-pressed={onTime} className={`cm-seg cm-seg--ontime${onTime ? ' is-on' : ''}`} onClick={() => pick('present', 'onTime')}>Đúng giờ</button>
              <button type="button" aria-pressed={late} className={`cm-seg cm-seg--late${late ? ' is-on' : ''}`} onClick={() => pick('present', 'lateTime')}>Đi muộn</button>
            </>
          )}
        </div>
      </div>
    </li>
  );
}
